import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  configureSparqlClient,
  testConnection,
  getCurrentProjectId,
  type SparqlClientConfig,
} from '@/lib/api/sparql-adapter'

// Preset configurations (kept for backward compatibility, but not used in multi-tenant mode)
export type ConnectionPreset = 'cloud' | 'hetzner' | 'local' | 'custom' | 'api'

export interface FusekiPreset {
  name: string
  description: string
  config: SparqlClientConfig
}

export const CONNECTION_PRESETS: Record<ConnectionPreset, FusekiPreset> = {
  api: {
    name: 'API Mode',
    description: 'Connected via iSpot API (multi-tenant)',
    config: { accountId: null, projectId: null },
  },
  cloud: {
    name: 'Hetzner Cloud (Legacy)',
    description: 'Direct Fuseki connection (deprecated)',
    config: { accountId: null, projectId: null },
  },
  hetzner: {
    name: 'Hetzner Cloud (Legacy)',
    description: 'Direct Fuseki connection (deprecated)',
    config: { accountId: null, projectId: null },
  },
  local: {
    name: 'Local Development (Legacy)',
    description: 'Direct Fuseki connection (deprecated)',
    config: { accountId: null, projectId: null },
  },
  custom: {
    name: 'Custom (Legacy)',
    description: 'Direct Fuseki connection (deprecated)',
    config: { accountId: null, projectId: null },
  },
}

interface ConnectionState {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  config: SparqlClientConfig
  activePreset: ConnectionPreset
  autoConnect: boolean
  setConfig: (config: Partial<SparqlClientConfig>) => void
  setPreset: (preset: ConnectionPreset) => void
  setAutoConnect: (autoConnect: boolean) => void
  connect: () => Promise<boolean>
  disconnect: () => void
  initialize: () => Promise<void>
}

// Default to API mode
const DEFAULT_CONFIG: SparqlClientConfig = { accountId: null, projectId: null }

export const useConnectionStore = create<ConnectionState>()(
  persist(
    (set, get) => ({
      isConnected: false,
      isConnecting: false,
      error: null,
      config: DEFAULT_CONFIG,
      activePreset: 'api',
      autoConnect: true,

      setConfig: (newConfig) => {
        const config = { ...get().config, ...newConfig }
        set({ config })
        configureSparqlClient(config)
      },

      setPreset: (preset) => {
        const presetConfig = CONNECTION_PRESETS[preset].config
        set({ activePreset: preset, config: presetConfig })
        configureSparqlClient(presetConfig)
      },

      setAutoConnect: (autoConnect) => {
        set({ autoConnect })
      },

      connect: async () => {
        set({ isConnecting: true, error: null })

        // In API mode, connection depends on having a project selected
        const projectId = getCurrentProjectId()
        if (!projectId) {
          set({
            isConnected: false,
            isConnecting: false,
            error: 'No project selected. Please select a project first.',
          })
          return false
        }

        try {
          const connected = await testConnection()
          set({ isConnected: connected, isConnecting: false })
          if (!connected) {
            set({ error: 'Could not connect to project' })
          }
          return connected
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Connection failed'
          set({ isConnected: false, isConnecting: false, error })
          return false
        }
      },

      disconnect: () => {
        set({ isConnected: false, error: null })
      },

      initialize: async () => {
        // In API mode, we check if a project is selected
        const projectId = getCurrentProjectId()
        if (projectId) {
          set({ isConnecting: true })
          try {
            const connected = await testConnection()
            set({ isConnected: connected, isConnecting: false })
          } catch {
            set({ isConnected: false, isConnecting: false })
          }
        }
      },
    }),
    {
      name: 'intellispot-connection-v2',
      partialize: (state) => ({
        activePreset: state.activePreset,
        autoConnect: state.autoConnect,
      }),
    }
  )
)
